import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { Placement } from '@atlaskit/popper/main';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { UserProfileCardForMention } from '@atlassian/user-profile-card/mention';

import { expValEquals } from '@confluence/feature-experiments';
import {
	type ProfileCardGetActions,
	getProfileCardProvider,
} from '@confluence/fabric-media-support/entry-points/ProfileCardProvider.ts';
import { FeedFollow } from '@confluence/profile/entry-points/FeedFollow.ts';
import { useFeedFollow } from '@confluence/profile/entry-points/useFeedFollow.ts';
import { getProfileClient } from '@confluence/profile/entry-points/getProfileClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useGetNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/useGetNavigateToTeamsApp.ts';
import type { ProfileCardProvider } from '@confluence/fabric-media-support/entry-points/types.ts';

type UseProfileCardProviderProps = {
	push: (url: string, forceReload?: boolean | undefined) => void;
	userId: string | null;
	cloudId: string;
	/**
	 * @deprecated This is no longer used - we should clean it up in a future update
	 */
	featureFlags?: Record<string, string | boolean>;
};

export function useProfilecardProvider({
	userId,
	cloudId,
	featureFlags,
}: UseProfileCardProviderProps): Promise<ProfileCardProvider> {
	const intl = useIntl();
	const feedFollowData = useFeedFollow();
	const getNavigateToTeamsApp = useGetNavigateToTeamsApp();
	const { orgId, userId: requestingUserId } = useSessionData();

	const handleProfileCardActions: (...args: any) => ProfileCardGetActions = useCallback(
		(loggedInUserId) => (profileCardUserId) => {
			const teamsAppPersonProfileUrl = getNavigateToTeamsApp({
				type: 'USER',
				payload: {
					userId: profileCardUserId,
				},
			}).href;

			const followed = !!(
				profileCardUserId && feedFollowData?.followedUsers?.includes(profileCardUserId)
			);
			return [
				{
					label: intl.formatMessage({ id: 'view.profile' }),
					callback: undefined,
					target: '_blank',
					link: teamsAppPersonProfileUrl,
				},
				{
					label: <FeedFollow followed={followed} />,
					id: 'feed-follow-user',
					callback: feedFollowData.getToggleFollowUser(profileCardUserId, followed, 'editor'),
					shouldRender: (userData?: { status: string }) =>
						feedFollowData.renderFeedFollow &&
						Boolean(profileCardUserId) &&
						profileCardUserId !== loggedInUserId &&
						userData?.status === 'active',
				},
			];
		},
		[feedFollowData, getNavigateToTeamsApp, intl],
	);

	return useMemo(() => {
		const providerPromise = getProfileCardProvider(
			cloudId,
			handleProfileCardActions(userId),
			featureFlags ?? {},
		);

		if (
			!(
				fg('people-teams_migrate-user-profile-card') ||
				expValEquals('pt_user_profile_card_migration_exp', 'isEnabled', true)
			)
		) {
			return providerPromise;
		}

		return providerPromise.then((provider) => ({
			...provider,
			renderUserMentionCard: ({
				userId: mentionUserId,
				cloudId: mentionCloudId,
				children,
				referenceElement,
				placement,
				isReduced,
			}: {
				userId: string;
				cloudId: string;
				children: React.ReactNode;
				referenceElement?: HTMLElement;
				placement?: Placement;
				isReduced?: boolean;
			}) => (
				<UserProfileCardForMention
					userId={mentionUserId}
					cloudId={mentionCloudId}
					orgId={orgId ?? ''}
					viewerId={requestingUserId ?? ''}
					referenceElement={referenceElement}
					placement={placement}
					isReduced={isReduced}
					agent={{ resourceClient: getProfileClient(mentionCloudId, featureFlags ?? {}) }}
				>
					{children}
				</UserProfileCardForMention>
			),
		}));
	}, [cloudId, handleProfileCardActions, userId, featureFlags, orgId, requestingUserId]);
}
